import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Heart, 
  Smile, 
  ShoppingBag, 
  Fuel, 
  Gift, 
  Utensils,
  Search,
  Filter,
  ChevronRight,
  Users,
  Clock
} from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { EmptyState } from '../../components/ui/EmptyState';
import { LoadingGridCard } from '../../components/ui/LoadingCard';
import { api } from '../../lib/api';
import { ENDPOINTS } from '../../config/api';
import { formatCurrency } from '../../lib/utils';

interface BenefitPlan {
  id: string;
  name: string;
  description: string;
  category: string;
  monthlyPrice: string;
  dependentPrice: string;
  maxDependents: number;
  waitingPeriodDays: number;
  logoUrl?: string;
  template?: {
    id: string;
    name: string;
  };
}

interface Category {
  category: string;
  count: number;
}

const categoryIcons: Record<string, React.ReactNode> = {
  HEALTH: <Heart className="w-6 h-6" />,
  DENTAL: <Smile className="w-6 h-6" />,
  SUPERMARKET: <ShoppingBag className="w-6 h-6" />,
  GAS_STATION: <Fuel className="w-6 h-6" />,
  GIFT_CARD: <Gift className="w-6 h-6" />,
  MEAL: <Utensils className="w-6 h-6" />,
};

const categoryLabels: Record<string, string> = {
  HEALTH: 'Plano de Saúde',
  DENTAL: 'Plano Odontológico',
  SUPERMARKET: 'Rede de Supermercados',
  GAS_STATION: 'Rede de Postos',
  GIFT_CARD: 'Vale Presente',
  MEAL: 'Vale Refeição',
  PHARMACY: 'Farmácia',
  EDUCATION: 'Educação',
  WELLNESS: 'Bem-estar',
  OTHER: 'Outros',
};

const categoryColors: Record<string, string> = {
  HEALTH: 'bg-red-500/20 text-red-400',
  DENTAL: 'bg-blue-500/20 text-blue-400',
  SUPERMARKET: 'bg-green-500/20 text-green-400',
  GAS_STATION: 'bg-orange-500/20 text-orange-400',
  GIFT_CARD: 'bg-purple-500/20 text-purple-400',
  MEAL: 'bg-yellow-500/20 text-yellow-400',
  PHARMACY: 'bg-pink-500/20 text-pink-400',
  EDUCATION: 'bg-indigo-500/20 text-indigo-400',
  WELLNESS: 'bg-teal-500/20 text-teal-400',
  OTHER: 'bg-gray-500/20 text-gray-400',
};

export function Benefits() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const { data: categories = [], isLoading: loadingCategories } = useQuery<Category[]>({
    queryKey: ['benefit-categories'],
    queryFn: async () => {
      const response = await api.get(ENDPOINTS.benefits.categories);
      return response.data;
    },
  });

  const { data: plans = [], isLoading: loadingPlans } = useQuery<BenefitPlan[]>({
    queryKey: ['benefit-plans', selectedCategory, search],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (selectedCategory) params.append('category', selectedCategory);
      if (search) params.append('search', search);
      const response = await api.get(`${ENDPOINTS.benefits.catalog}?${params}`);
      return response.data;
    },
  });

  const handlePlanClick = (planId: string) => {
    navigate(`/banking/benefits/${planId}`);
  };

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Benefícios</h1>
        <p className="text-surface-400 mt-1">
          Conheça os benefícios disponíveis e faça sua adesão
        </p>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-wrap gap-4 items-end">
        <div className="flex-1 min-w-[200px]">
          <Input
            placeholder="Buscar benefícios..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            leftIcon={<Search className="w-4 h-4" />}
          />
        </div>
        <Button
          variant="secondary"
          onClick={() => setSelectedCategory(null)}
          className={!selectedCategory ? 'ring-2 ring-primary-500' : ''}
        >
          <Filter className="w-4 h-4 mr-2" />
          Todos
        </Button>
      </div>

      {/* Categories */}
      {!loadingCategories && categories.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {categories.map((cat) => (
            <button
              key={cat.category}
              onClick={() => setSelectedCategory(
                selectedCategory === cat.category ? null : cat.category
              )}
              className={`
                flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-300
                ${selectedCategory === cat.category
                  ? 'bg-primary-500/20 text-primary-400 ring-2 ring-primary-500 shadow-lg shadow-primary-500/10'
                  : 'bg-surface-800 text-surface-300 hover:bg-surface-700'
                }
              `}
            >
              {categoryIcons[cat.category] || <Gift className="w-5 h-5" />}
              <span>{categoryLabels[cat.category] || cat.category}</span>
              <Badge variant="info" size="sm">{cat.count}</Badge>
            </button>
          ))}
        </div>
      )}

      {/* Plans Grid */}
      {loadingPlans ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <LoadingGridCard key={i} className="h-64" />
          ))}
        </div>
      ) : plans.length === 0 ? (
        <EmptyState
          icon={Gift}
          title="Nenhum benefício encontrado"
          message={search || selectedCategory
            ? 'Tente ajustar os filtros de busca'
            : 'Não há benefícios disponíveis no momento'}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {plans.map((plan, index) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card
                hover
                glow
                className="cursor-pointer group h-full flex flex-col"
                onClick={() => handlePlanClick(plan.id)}
              >
                {/* Category Badge */}
                <div className="flex items-center justify-between mb-4">
                  <span className={`
                    inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium
                    ${categoryColors[plan.category] || categoryColors.OTHER}
                  `}>
                    {categoryIcons[plan.category] || <Gift className="w-3.5 h-3.5" />}
                    {categoryLabels[plan.category] || plan.category}
                  </span>
                  <ChevronRight className="w-5 h-5 text-surface-500 group-hover:text-primary-400 transition-colors" />
                </div>

                {/* Plan Info */}
                <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-primary-400 transition-colors">
                  {plan.name}
                </h3>
                <p className="text-surface-400 text-sm mb-4 line-clamp-2 flex-1">
                  {plan.description || 'Sem descrição disponível'}
                </p>

                {/* Price */}
                <div className="border-t border-surface-700 pt-4 mt-auto">
                  <div className="flex items-baseline gap-1">
                    <span className="text-2xl font-bold text-primary-400">
                      {formatCurrency(Number(plan.monthlyPrice) / 100)}
                    </span>
                    <span className="text-surface-400 text-sm">/mês</span>
                  </div>
                  
                  {/* Features */}
                  <div className="flex items-center gap-4 mt-3 text-xs text-surface-400">
                    {plan.maxDependents > 0 && (
                      <span className="flex items-center gap-1">
                        <Users className="w-3.5 h-3.5" />
                        Até {plan.maxDependents} dependentes
                      </span>
                    )}
                    {plan.waitingPeriodDays > 0 && (
                      <span className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        Carência {plan.waitingPeriodDays} dias
                      </span>
                    )}
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}

export default Benefits;
